import React from 'react';
import { Link } from 'react-router-dom';
import { Landmark, Crown, Globe2, Swords, Palette, Castle, Map, Briefcase, Database, Building2 } from 'lucide-react';
import { publications, categoryConfig } from '../../data/publications';

const publicationIconMap: Record<string, React.ElementType> = {
  'Landmark': Landmark,
  'Crown': Crown,
  'Globe2': Globe2,
  'Swords': Swords,
  'Palette': Palette,
  'Building2': Building2,
  'Castle': Castle,
  'Map': Map,
  'Briefcase': Briefcase,
  'Database': Database,
};

const categoryImages: Record<string, string> = {
  'foundational-histories': '/foundational-histories.jpg.png',
  'governance': '/governance.jpg.png',
  'yoruba-origins': '/yoruba-origins.jpg.png',
  'conflicts': '/conflicts.jpg.png',
  'culture-heritage': '/culture-heritage.jpg.png',
  'urban-development': '/urban-development.jpg.png',
  'palace-tourism': '/palace-tourism.jpg.png',
  'land-property': '/land-property.jpg.png',
  'local-government': '/local-government.jpg.png',
  'reference-data': '/reference-data.jpg.png',
};

export default function CollectionsPage() {
  const categoryPublicationCounts = Object.keys(categoryConfig).reduce((acc, slug) => {
    acc[slug] = publications.filter(pub => pub.category === slug).length;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="min-h-screen bg-white pt-20">
      <div className="bg-gradient-to-r from-[#064635] to-[#064635]/90 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Publication Collections
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Explore our comprehensive collection of 72+ publications across 10 specialized categories covering Ibadan's rich history, governance, culture, and development.
          </p>
        </div>
      </div>

      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(categoryConfig).map(([slug, config]) => {
              const Icon = publicationIconMap[config.icon];
              const count = categoryPublicationCounts[slug];
              const image = categoryImages[slug];

              return (
                <Link
                  key={slug}
                  to={`/collections/${slug}`}
                  className="group relative overflow-hidden rounded-xl shadow-md hover:shadow-2xl
                           transition duration-300 transform hover:-translate-y-1"
                >
                  <div className="aspect-w-16 aspect-h-12">
                    <img
                      src={image}
                      alt={config.title}
                      className="w-full h-64 object-cover group-hover:scale-110 transition duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#064635] via-[#064635]/70 to-transparent">
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="flex items-center justify-between mb-3">
                        <div className="w-10 h-10 rounded-lg bg-[#D4AF37] flex items-center justify-center
                                      transform group-hover:scale-110 transition duration-300">
                          <Icon className="h-5 w-5 text-[#064635]" />
                        </div>
                        <span className="px-3 py-1 bg-[#D4AF37]/20 backdrop-blur-sm rounded-full
                                       text-[#D4AF37] text-sm font-semibold border border-[#D4AF37]/30">
                          {count} {count === 1 ? 'publication' : 'publications'}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2 group-hover:text-[#D4AF37] transition">
                        {config.title}
                      </h3>
                      <p className="text-gray-300 text-sm leading-relaxed line-clamp-2">
                        {config.description}
                      </p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
